import type { SubmissionColumn, SubmissionRow, TriageStatus } from '../../api/forms';
import { formatCompositeAnswer } from '../../shared/address';

// ---- triage (P5): the four states, how they read and how they look ----
export const TRIAGE_LABEL: Record<TriageStatus, string> = {
  new: 'New', in_review: 'In review', done: 'Done', spam: 'Spam',
};
/** fs-pill intents: new asks for attention, in review is in hand, done is settled, spam is put away. */
export const TRIAGE_INTENT: Record<TriageStatus, string> = {
  new: 'info', in_review: 'caution', done: 'ok', spam: 'neutral',
};
export const triageOf = (row: Pick<SubmissionRow, 'triage_status'>): TriageStatus => row.triage_status || 'new';

/**
 * Reading a column id off a submission.
 *
 * The server decides which columns EXIST — it derives them from the published
 * definition and uses the same list as the filter whitelist. This side only
 * turns an id into something to show in a cell, so the two cannot drift on
 * which columns are legal, only on how one is rendered.
 */

/** A repeating group's instances, whatever depth the id names. */
const instances = (row: SubmissionRow, scope: string): Record<string, unknown>[] => {
  const v = (row.data || {})[scope];
  return Array.isArray(v) ? (v as Record<string, unknown>[]) : [];
};

const numeric = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v ?? '').trim();
  return s && /^-?\d+(\.\d+)?$/.test(s) ? Number(s) : null;
};

/** A field column's value: the key, or one row of a matrix answer when the key is `key.row` (P3). */
export function fieldValue(data: Record<string, unknown>, key: string): unknown {
  const dot = key.indexOf('.');
  if (dot < 0) return data[key];
  const head = data[key.slice(0, dot)];
  return head && typeof head === 'object' && !Array.isArray(head) ? (head as Record<string, unknown>)[key.slice(dot + 1)] : undefined;
}

export interface CellValue {
  /** what to print */
  text: string;
  /** right-align and never highlight-match a number */
  numeric?: boolean;
}

export function readColumn(row: SubmissionRow, column: SubmissionColumn): CellValue {
  const id = column.id;

  if (column.kind === 'meta') {
    switch (id) {
      case 'meta:submissionId': return { text: row.submission_id };
      case 'meta:createdAt': return { text: formatWhen(row.created_at) };
      case 'meta:editedAt': return { text: row.edited_at ? formatWhen(row.edited_at) : '—' };
      case 'meta:submitter': return { text: row.submitter?.email || row.submitter?.name || '—' };
      case 'meta:triageStatus': return { text: TRIAGE_LABEL[triageOf(row)] };
      case 'meta:assignee': return { text: row.assignee?.name || row.assignee?.email || '—' };
      case 'meta:isTest': return { text: row.is_test ? 'Test' : '—' };
      default: return { text: '' };
    }
  }

  if (column.kind === 'field') {
    return { text: display(fieldValue(row.data || {}, column.key || '')), numeric: !!column.numeric };
  }

  const [, scope, kind, fieldKey] = id.split(':');
  const rows = instances(row, scope);
  if (kind === 'count') {
    // The count comes from the key the submission stores it under, because a
    // NESTED group has no array of its own at the top of the data — its
    // instances sit inside each parent. Measuring the array is the fallback
    // for a submission saved without the count key.
    const stored = numeric((row.data || {})[column.key || '']);
    return { text: String(stored ?? rows.length), numeric: true };
  }
  if (kind === 'sum') {
    // A non-numeric answer contributes nothing rather than poisoning the total,
    // matching what the server's sum does.
    const total = rows.reduce((n, inst) => n + (numeric(inst[fieldKey]) ?? 0), 0);
    return { text: rows.length ? String(total) : '—', numeric: true };
  }
  return { text: display(rows[0]?.[fieldKey]), numeric: !!column.numeric };
}

/** One answer as a single line of text. */
function display(v: unknown): string {
  if (v === undefined || v === null || v === '') return '—';
  if (Array.isArray(v)) {
    // A list of objects is a repeating group; its own columns cover it.
    const flat = v.filter((x) => x === null || typeof x !== 'object');
    return flat.length === v.length ? flat.join(', ') : `${v.length} item${v.length === 1 ? '' : 's'}`;
  }
  if (typeof v === 'object') return formatCompositeAnswer(v) ?? '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

/** "8/22 9:02 AM" — the table has no room for a full timestamp. */
export function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${date} ${time}`;
}

/** The full timestamp, for a `title` and for the drawer. */
export const formatFull = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

// ---------------------------------------------------------------------------
// The Deliveries cell
// ---------------------------------------------------------------------------

export type DeliveryState = 'all_delivered' | 'any_failed' | 'pending' | 'partial' | 'none_sent';

export const DELIVERY_STYLE: Record<DeliveryState, { background: string; color: string }> = {
  any_failed: { background: '#fed7d7', color: '#742a2a' },
  pending: { background: '#fefcbf', color: '#744210' },
  all_delivered: { background: '#c6f6d5', color: '#22543d' },
  partial: { background: '#feebc8', color: '#975a16' },
  none_sent: { background: '#edf2f7', color: '#4a5568' },
};

/** The same colors as semantic intents, for the token-based `fs-pill` classes. */
export const DELIVERY_INTENT: Record<DeliveryState, string> = {
  any_failed: 'danger',
  pending: 'caution',
  all_delivered: 'ok',
  partial: 'warn',
  none_sent: 'neutral',
};

/** The pill's words, straight from the counts. */
export function deliveryPillText(s: {
  state: DeliveryState; total: number; delivered: number; failed: number; pending: number; skipped: number;
}): string {
  switch (s.state) {
    case 'any_failed': return `${s.failed} of ${s.total} failed`;
    case 'pending': return `${s.pending} pending`;
    case 'all_delivered': return `${s.delivered} delivered`;
    case 'partial': return `${s.delivered} of ${s.total}${s.skipped ? ` · ${s.skipped} skipped` : ''}`;
    default: return 'nothing sent';
  }
}

/**
 * The Received cell (R8, screen 18): "Today 2:47 PM", "Yesterday 11:12 AM",
 * then the date with its time — the full timestamp stays in the cell's title.
 */
export function formatReceived(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86_400_000);
  if (diff === 0) return `Today ${time}`;
  if (diff === 1) return `Yesterday ${time}`;
  const sameYear = d.getFullYear() === now.getFullYear();
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) })} ${time}`;
}
